// @refresh reload

import * as Sentry from "@sentry/solid"
import { render } from "solid-js/web"
import { AppBaseProviders, AppInterface } from "@/app"
import { type Platform, PlatformProvider } from "@/context/platform"
import { dict as en } from "@/i18n/en"
import { handleNotificationClick } from "@/utils/notification-click"
import { authFromToken, setWallarmClientId, setWallarmXssToken } from "@/utils/server"
import pkg from "../package.json"
import { ServerConnection } from "./context/server"

const DEFAULT_SERVER_URL_KEY = "opencode.settings.dat:defaultServerUrl"

// English-only build: the root-not-found dev error is always English.
const getRootNotFoundError = () => {
  const key = "error.dev.rootNotFound" as const
  return en[key]
}

const getStorage = (key: string) => {
  if (typeof localStorage === "undefined") return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const setStorage = (key: string, value: string | null) => {
  if (typeof localStorage === "undefined") return
  try {
    if (value !== null) {
      localStorage.setItem(key, value)
      return
    }
    localStorage.removeItem(key)
  } catch {
    return
  }
}

const readDefaultServerUrl = () => getStorage(DEFAULT_SERVER_URL_KEY)
const writeDefaultServerUrl = (url: string | null) => setStorage(DEFAULT_SERVER_URL_KEY, url)

const notify: Platform["notify"] = async (title, description, href) => {
  if (!("Notification" in window)) return

  const permission =
    Notification.permission === "default"
      ? await Notification.requestPermission().catch(() => "denied")
      : Notification.permission

  if (permission !== "granted") return

  const inView = document.visibilityState === "visible" && document.hasFocus()
  if (inView) return

  const notification = new Notification(title, {
    body: description ?? "",
    icon: "https://opencode.ai/favicon-96x96-v3.png",
  })

  notification.onclick = () => {
    handleNotificationClick(href)
    notification.close()
  }
}

const openLink: Platform["openLink"] = (url) => {
  window.open(url, "_blank")
}

const back: Platform["back"] = () => {
  window.history.back()
}

const forward: Platform["forward"] = () => {
  window.history.forward()
}

const restart: Platform["restart"] = async () => {
  window.location.reload()
}

const root = document.getElementById("root")
if (!(root instanceof HTMLElement) && import.meta.env.DEV) {
  throw new Error(getRootNotFoundError())
}

const getCurrentUrl = () => {
  if (location.hostname.includes("opencode.ai")) return "http://localhost:4096"
  if (import.meta.env.DEV)
    return `http://${import.meta.env.VITE_OPENCODE_SERVER_HOST ?? "localhost"}:${import.meta.env.VITE_OPENCODE_SERVER_PORT ?? "4096"}`
  // Production (Wallarm agentic-ui): the SPA owns its host and is served at the
  // root, with the chat-adapter reverse-proxied same-origin (see
  // infra/docker/agentic-ui-nginx). The opencode SDK string-concatenates this
  // base with "/session" etc., so the same-origin root routes every API call.
  return location.origin
}

const getDefaultUrl = () => {
  const lsDefault = readDefaultServerUrl()
  if (lsDefault) return lsDefault
  return getCurrentUrl()
}

const clearAuthToken = () => {
  const params = new URLSearchParams(location.search)
  if (!params.has("auth_token")) return
  params.delete("auth_token")
  history.replaceState(null, "", location.pathname + (params.size ? `?${params}` : "") + location.hash)
}

const platform: Platform = {
  platform: "web",
  version: pkg.version,
  openLink,
  back,
  forward,
  restart,
  notify,
  getDefaultServer: async () => {
    const stored = readDefaultServerUrl()
    return stored ? ServerConnection.Key.make(stored) : null
  },
  setDefaultServer: writeDefaultServerUrl,
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE ?? `web@${pkg.version}`,
    initialScope: {
      tags: {
        platform: "web",
      },
    },
    integrations: (integrations) => {
      return integrations.filter(
        (i) =>
          i.name !== "Breadcrumbs" && !(import.meta.env.OPENCODE_CHANNEL === "prod" && i.name === "GlobalHandlers"),
      )
    },
  })
}

// Wallarm: fetch the CSRF/XSS token the same way the main console does — on page
// load, using the wsess cookie. The chat-adapter backend requires it (as
// X-Wallarm-XSS-Token) alongside the cookie; without it every request 401s.
// The fetch is SAME-ORIGIN (/v1/token): nginx proxies it to the configured
// Wallarm API server-side (see infra/docker/agentic-ui-nginx). This avoids a
// cross-origin request — the dedicated agentic-chat.<env> host is NOT in the
// Wallarm cloud's CORS allowlist, so a direct cross-origin fetch to the API is
// blocked. The wsess cookie (.wallarm.com-scoped) rides on the same-origin
// request and is forwarded upstream by nginx. Best-effort: on any failure (local
// dev, no cookie) we render anyway and fall back to the auth_token path.
async function bootstrapWallarmXssToken(): Promise<void> {
  try {
    const res = await fetch("/v1/token", { credentials: "include" })
    if (!res.ok) return
    const body = (await res.json()) as { body?: { token?: string } }
    if (body.body?.token) setWallarmXssToken(body.body.token)
  } catch {
    // No Wallarm session reachable — leave the token unset.
  }
}

if (root instanceof HTMLElement) {
  void (async () => {
    await bootstrapWallarmXssToken()
    // The embedder (platform-ui) passes the active client id via the iframe URL
    // (`?client_id=`); store it so every SDK client sends X-Wallarm-Client-Id and
    // chat-adapter can scope to the run's tenant (not the wsess "home" client).
    const clientIdParam = new URLSearchParams(location.search).get("client_id")
    if (clientIdParam) setWallarmClientId(clientIdParam)
    const auth = authFromToken(new URLSearchParams(location.search).get("auth_token"))
    clearAuthToken()
    const server: ServerConnection.Http = {
      type: "http",
      authToken: !!auth,
      http: {
        url: getCurrentUrl(),
        ...auth,
      },
    }
    render(
      () => (
        <PlatformProvider value={platform}>
          <AppBaseProviders>
            <AppInterface
              defaultServer={ServerConnection.Key.make(getDefaultUrl())}
              canonicalLocalServer={ServerConnection.key(server)}
              servers={[server]}
              disableHealthCheck
            />
          </AppBaseProviders>
        </PlatformProvider>
      ),
      root,
    )
  })()
}
